import { X, Download, CheckCircle, AlertTriangle, XCircle, TrendingUp, GraduationCap, Briefcase, Code, Users, Wrench, Heart } from 'lucide-react';

// ─── Color mappings - White/Blue theme ─────────────────────────────────────
const gradeConfig = {
  'Excellent': { scoreBg: 'bg-emerald-50', scoreBorder: 'border-emerald-200', scoreText: 'text-emerald-700', badgeBg: 'bg-emerald-100', badgeText: 'text-emerald-800', bar: 'bg-emerald-500' },
  'Good':      { scoreBg: 'bg-blue-50',    scoreBorder: 'border-blue-200',    scoreText: 'text-[#2563eb]',  badgeBg: 'bg-blue-100',  badgeText: 'text-blue-800',  bar: 'bg-[#2563eb]' },
  'Fair':      { scoreBg: 'bg-amber-50',   scoreBorder: 'border-amber-200',   scoreText: 'text-amber-700',  badgeBg: 'bg-amber-100', badgeText: 'text-amber-800', bar: 'bg-amber-500' },
  'Poor':      { scoreBg: 'bg-red-50',     scoreBorder: 'border-red-200',     scoreText: 'text-red-700',    badgeBg: 'bg-red-100',   badgeText: 'text-red-800',   bar: 'bg-red-500' },
};

const matchTypeBadge = {
  'Exact':        'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Equivalent':   'bg-blue-100 text-[#2563eb] border-blue-200',
  'Transferable': 'bg-amber-100 text-amber-700 border-amber-200',
  'Missing':      'bg-red-100 text-red-600 border-red-200',
};

const criteriaIcons = {
  technical_skills: Code, experience_level: Briefcase, education_certifications: GraduationCap,
  domain_knowledge: TrendingUp, soft_skills: Users, tools_platforms: Wrench, cultural_fit: Heart,
};

const criteriaLabels = {
  technical_skills: 'Technical Skills', experience_level: 'Experience Level',
  education_certifications: 'Education & Certifications', domain_knowledge: 'Domain Knowledge',
  soft_skills: 'Soft Skills', tools_platforms: 'Tools & Platforms', cultural_fit: 'Cultural Fit',
};

const criteriaIconColors = {
  technical_skills: 'text-[#2563eb] bg-blue-50',   experience_level: 'text-purple-600 bg-purple-50',
  education_certifications: 'text-emerald-600 bg-emerald-50', domain_knowledge: 'text-amber-600 bg-amber-50',
  soft_skills: 'text-pink-600 bg-pink-50',          tools_platforms: 'text-slate-600 bg-slate-100',
  cultural_fit: 'text-rose-600 bg-rose-50',
};

const scoreBarColor = (s) => s >= 80 ? 'bg-emerald-500' : s >= 60 ? 'bg-[#2563eb]' : s >= 40 ? 'bg-amber-500' : 'bg-red-500';
const scoreTextColor = (s) => s >= 80 ? 'text-emerald-700' : s >= 60 ? 'text-[#2563eb]' : s >= 40 ? 'text-amber-700' : 'text-red-600';

// ─── Export PDF helper ──────────────────────────────────────────────────────
function buildPrintHTML(candidate, overallScore, details, grade) {
  return `<html><head><title>Score Report - ${candidate.name}</title>
  <style>
    body{font-family:Georgia,serif;padding:40px;max-width:800px;margin:0 auto;color:#1e293b}
    h1{color:#1e293b;border-bottom:2px solid #2563eb;padding-bottom:10px}
    h2{color:#2563eb;margin-top:24px;font-size:16px}
    table{width:100%;border-collapse:collapse;margin:12px 0;font-size:13px}
    th,td{border:1px solid #e2e8f0;padding:8px 10px;text-align:left}
    th{background:#f8fafc;color:#475569}
    .score-header{background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;padding:16px;margin:16px 0}
    .badge{display:inline-block;padding:2px 10px;border-radius:20px;font-size:12px;font-weight:600}
    .excellent{background:#d1fae5;color:#065f46} .good{background:#dbeafe;color:#1e40af}
    .fair{background:#fef3c7;color:#92400e} .poor{background:#fee2e2;color:#991b1b}
    ul{margin:6px 0;padding-left:18px} li{margin:3px 0;font-size:13px;color:#475569}
  </style></head><body>
  <h1>Resume Score Report</h1>
  <p><strong>Candidate:</strong> ${candidate.name} | <strong>Email:</strong> ${candidate.email} | <strong>Date:</strong> ${new Date().toLocaleDateString()}</p>
  <div class="score-header">
    <strong style="font-size:32px">${overallScore}</strong><span style="color:#64748b">/100</span>&nbsp;&nbsp;
    <span class="badge ${grade.toLowerCase()}">${grade}</span>&nbsp;&nbsp;
    <span style="color:#475569">Recommendation: ${details.recommendation || 'N/A'}</span>
  </div>
  ${details.criteria_scores ? `<h2>Criteria Breakdown</h2><table><tr><th>Criteria</th><th>Score/100</th><th>Weight</th><th>Weighted</th></tr>${Object.entries(details.criteria_scores).map(([k,v])=>`<tr><td>${criteriaLabels[k]||k}</td><td>${v.score||0}</td><td>${v.weight||0}%</td><td>${((v.score||0)*(v.weight||0)/100).toFixed(1)}</td></tr>`).join('')}</table>` : ''}
  ${details.skill_scores?.length ? `<h2>Skill-Level Scoring</h2><table><tr><th>Skill</th><th>Required</th><th>Candidate</th><th>Match</th><th>Score</th><th>Evidence</th></tr>${details.skill_scores.slice(0,20).map(s=>`<tr><td>${s.skill}</td><td>${s.required_level||'-'}</td><td>${s.candidate_level||'-'}</td><td>${s.match_type}</td><td>${s.score}</td><td>${s.evidence||'-'}</td></tr>`).join('')}</table>` : ''}
  ${details.detailed_summary ? `<h2>Strengths</h2><ul>${(details.detailed_summary.strengths||[]).map(s=>`<li>${s}</li>`).join('')}</ul><h2>Concerns</h2><ul>${(details.detailed_summary.concerns||[]).map(c=>`<li>${c}</li>`).join('')}</ul>` : ''}
  </body></html>`;
}

// ─── Main Component ─────────────────────────────────────────────────────────
export default function ScoreDetailModal({ candidate, onClose }) {
  const details = candidate?.score_details || {};
  const grade = details.grade || 'Fair';
  const cfg = gradeConfig[grade] || gradeConfig['Fair'];
  const overallScore = details.overall_score || (candidate?.score ? Math.round(candidate.score * 10) : 50);

  const handleExportPDF = () => {
    const html = buildPrintHTML(candidate, overallScore, details, grade);
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank');
    if (w) {
      const doPrint = () => { w.print(); URL.revokeObjectURL(url); };
      w.addEventListener('load', doPrint);
      setTimeout(() => { URL.revokeObjectURL(url); }, 10000); // safety cleanup
    } else {
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      style={{ fontFamily: 'Georgia, serif' }}
    >
      <div
        className="bg-white border border-slate-200 rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={e => e.stopPropagation()}
        data-testid="score-detail-modal"
      >
        {/* ── HEADER ─────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white">
          <div>
            <h2 className="text-lg font-bold text-slate-800">{candidate.name}</h2>
            <p className="text-slate-400 text-xs mt-0.5">{candidate.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportPDF}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200 transition-colors"
              data-testid="export-pdf-btn"
            >
              <Download className="w-3.5 h-3.5" /> Export PDF
            </button>
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* ── SCROLLABLE CONTENT ─────────────────────────────────────────── */}
        <div className="flex-1 overflow-auto bg-slate-50" id="score-report-content">
          <div className="p-6 space-y-5">

            {/* ── OVERALL SCORE HERO ──────────────────────────────────────── */}
            <div className={`rounded-2xl border p-5 ${cfg.scoreBg} ${cfg.scoreBorder}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-5">
                  {/* Big score number */}
                  <div className="text-center">
                    <p className={`text-5xl font-bold ${cfg.scoreText}`}>{overallScore}</p>
                    <p className="text-xs text-slate-500 mt-0.5">out of 100</p>
                  </div>
                  <div className="h-14 w-px bg-slate-200" />
                  {/* Grade + description */}
                  <div>
                    <span className={`inline-flex px-3 py-1 rounded-full text-sm font-semibold ${cfg.badgeBg} ${cfg.badgeText} mb-1`}>
                      {grade}
                    </span>
                    <p className="text-sm text-slate-600">
                      {grade === 'Excellent' && 'Strong fit. Fast-track to interview.'}
                      {grade === 'Good' && 'Solid candidate. Proceed to interview.'}
                      {grade === 'Fair' && 'Partial fit. Consider with reservations.'}
                      {grade === 'Poor' && 'Significant mismatch. Do not proceed.'}
                    </p>
                  </div>
                </div>
                {/* Recommendation */}
                <div className="text-right">
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Recommendation</p>
                  <p className={`text-sm font-semibold ${cfg.scoreText}`}>{details.recommendation || 'N/A'}</p>
                </div>
              </div>
              {/* Progress bar */}
              <div className="w-full bg-white rounded-full h-2.5 shadow-inner">
                <div
                  className={`h-2.5 rounded-full transition-all duration-700 ${cfg.bar}`}
                  style={{ width: `${overallScore}%` }}
                />
              </div>
            </div>

            {/* ── CRITERIA BREAKDOWN ──────────────────────────────────────── */}
            {details.criteria_scores && Object.keys(details.criteria_scores).length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Criteria Breakdown</h3>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {Object.entries(details.criteria_scores).map(([key, val]) => {
                    const Icon = criteriaIcons[key] || Code;
                    const score = val?.score || 0;
                    const iconCls = criteriaIconColors[key] || 'text-slate-600 bg-slate-100';
                    return (
                      <div key={key} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-2 mb-3">
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${iconCls}`}>
                            <Icon className="w-3.5 h-3.5" />
                          </div>
                          <span className="text-xs font-medium text-slate-600 leading-tight">{criteriaLabels[key] || key}</span>
                        </div>
                        <div className="flex items-end gap-1 mb-2">
                          <span className={`text-2xl font-bold ${scoreTextColor(score)}`}>{score}</span>
                          <span className="text-xs text-slate-400 mb-0.5">/ 100 ({val?.weight || 0}%)</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full transition-all duration-700 ${scoreBarColor(score)}`}
                            style={{ width: `${score}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── SKILL-LEVEL SCORING TABLE ───────────────────────────────── */}
            {details.skill_scores?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Skill-Level Scoring</h3>
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                  <div className="grid grid-cols-[1.2fr_80px_80px_100px_60px_1fr] gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[10px] text-slate-500 font-semibold uppercase tracking-wider">
                    <span>Skill</span><span>Required</span><span>Candidate</span><span>Match Type</span><span>Score</span><span>Evidence</span>
                  </div>
                  <div className="max-h-52 overflow-auto divide-y divide-slate-100">
                    {details.skill_scores.map((skill) => (
                      <div key={skill.skill} className="grid grid-cols-[1.2fr_80px_80px_100px_60px_1fr] gap-2 px-4 py-2.5 text-xs items-center hover:bg-slate-50 transition-colors">
                        <span className="text-slate-800 font-semibold truncate">{skill.skill}</span>
                        <span className="text-slate-500">{skill.required_level || '—'}</span>
                        <span className="text-slate-600">{skill.candidate_level || '—'}</span>
                        <span>
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${matchTypeBadge[skill.match_type] || 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                            {skill.match_type}
                          </span>
                        </span>
                        <span className={`font-bold ${scoreTextColor(skill.score)}`}>{skill.score}</span>
                        <span className="text-slate-400 truncate text-[11px]" title={skill.evidence}>{skill.evidence || '—'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── SKILLS MATCHED + GAPS ───────────────────────────────────── */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Skills Matched */}
              {details.skills_matched && (
                <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                  <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500" /> Skills Matched
                  </h3>
                  <div className="space-y-3 text-xs">
                    {details.skills_matched.technical?.length > 0 && (
                      <div>
                        <p className="text-[#2563eb] font-semibold mb-1.5 text-[11px] uppercase tracking-wide">Technical</p>
                        <div className="flex flex-wrap gap-1.5">
                          {details.skills_matched.technical.map((s) => (
                            <span key={s} className="bg-blue-50 text-[#2563eb] border border-blue-200 px-2 py-0.5 rounded-full text-[11px] font-medium">{s}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {details.skills_matched.tools?.length > 0 && (
                      <div>
                        <p className="text-slate-600 font-semibold mb-1.5 text-[11px] uppercase tracking-wide">Tools</p>
                        <div className="flex flex-wrap gap-1.5">
                          {details.skills_matched.tools.map((s) => (
                            <span key={s} className="bg-slate-100 text-slate-600 border border-slate-200 px-2 py-0.5 rounded-full text-[11px] font-medium">{s}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {details.skills_matched.soft_skills?.length > 0 && (
                      <div>
                        <p className="text-emerald-700 font-semibold mb-1.5 text-[11px] uppercase tracking-wide">Soft Skills</p>
                        <div className="flex flex-wrap gap-1.5">
                          {details.skills_matched.soft_skills.map((s) => (
                            <span key={s} className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full text-[11px] font-medium">{s}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {!details.skills_matched.technical?.length && !details.skills_matched.tools?.length && !details.skills_matched.soft_skills?.length && (
                      <p className="text-slate-400 text-xs">No skill matches recorded.</p>
                    )}
                  </div>
                </div>
              )}

              {/* Skill Gaps */}
              {details.skill_gaps && (
                <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                  <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" /> Skill Gaps
                  </h3>
                  <div className="space-y-3">
                    {details.skill_gaps.critical?.length > 0 && (
                      <div>
                        <p className="text-red-600 font-semibold mb-1.5 text-[11px] uppercase tracking-wide flex items-center gap-1">
                          <XCircle className="w-3 h-3" /> Critical Gaps
                        </p>
                        <div className="space-y-2">
                          {details.skill_gaps.critical.map((g) => (
                            <div key={g.skill} className="bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                              <p className="text-red-700 font-semibold text-xs">{g.skill}</p>
                              <p className="text-red-500 text-[11px] mt-0.5">{g.impact}</p>
                              {g.remediation && <p className="text-slate-400 text-[11px] mt-0.5 italic">→ {g.remediation}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {details.skill_gaps.minor?.length > 0 && (
                      <div>
                        <p className="text-amber-700 font-semibold mb-1.5 text-[11px] uppercase tracking-wide">Minor Gaps</p>
                        <div className="flex flex-wrap gap-1.5">
                          {details.skill_gaps.minor.map((g) => (
                            <span key={g.skill} className="bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full text-[11px] font-medium" title={g.impact}>{g.skill}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {!details.skill_gaps.critical?.length && !details.skill_gaps.minor?.length && (
                      <div className="flex items-center gap-2 text-emerald-700">
                        <CheckCircle className="w-4 h-4" />
                        <p className="text-xs font-medium">No significant skill gaps identified.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* ── EXPERIENCE + EDUCATION ANALYSIS ────────────────────────── */}
            <div className="grid md:grid-cols-2 gap-4">
              {details.experience_analysis && Object.keys(details.experience_analysis).length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                  <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-[#2563eb]" /> Experience Analysis
                  </h3>
                  <div className="space-y-2.5">
                    {[
                      { label: 'Total Years', val: details.experience_analysis.total_years },
                      { label: 'Relevant Years', val: details.experience_analysis.relevant_years },
                      { label: 'Required Years', val: details.experience_analysis.required_years },
                    ].map((item) => (
                      <div key={item.label} className="flex justify-between items-center py-1.5 border-b border-slate-100">
                        <span className="text-xs text-slate-500">{item.label}</span>
                        <span className="text-xs font-semibold text-slate-800">{item.val ?? '—'}</span>
                      </div>
                    ))}
                    {details.experience_analysis.industry_relevance && (
                      <div className="flex justify-between items-center py-1.5 border-b border-slate-100">
                        <span className="text-xs text-slate-500">Industry Relevance</span>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          details.experience_analysis.industry_relevance === 'Same' ? 'bg-emerald-100 text-emerald-700' :
                          details.experience_analysis.industry_relevance === 'Adjacent' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-600'
                        }`}>{details.experience_analysis.industry_relevance}</span>
                      </div>
                    )}
                    {details.experience_analysis.career_progression && (
                      <p className="text-xs text-slate-500 pt-1 italic">{details.experience_analysis.career_progression}</p>
                    )}
                  </div>
                </div>
              )}

              {details.education_match && Object.keys(details.education_match).length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                  <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-emerald-600" /> Education Match
                  </h3>
                  <div className="space-y-2.5">
                    {[
                      { label: 'Required Degree', val: details.education_match.required_degree },
                      { label: 'Candidate Degree', val: details.education_match.candidate_degree },
                    ].map((item) => (
                      <div key={item.label} className="flex justify-between items-center py-1.5 border-b border-slate-100">
                        <span className="text-xs text-slate-500">{item.label}</span>
                        <span className="text-xs font-semibold text-slate-700">{item.val || '—'}</span>
                      </div>
                    ))}
                    {details.education_match.match_level && (
                      <div className="flex justify-between items-center py-1.5 border-b border-slate-100">
                        <span className="text-xs text-slate-500">Match Level</span>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          details.education_match.match_level === 'Exact' ? 'bg-emerald-100 text-emerald-700' :
                          details.education_match.match_level === 'Equivalent' ? 'bg-blue-100 text-[#2563eb]' :
                          details.education_match.match_level === 'Related' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-600'
                        }`}>{details.education_match.match_level}</span>
                      </div>
                    )}
                    {details.education_match.certifications_matched?.length > 0 && (
                      <div>
                        <p className="text-emerald-600 text-[11px] font-semibold mb-1">✓ Certifications Matched</p>
                        <div className="flex flex-wrap gap-1">
                          {details.education_match.certifications_matched.map((c) => (
                            <span key={c} className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full text-[11px]">{c}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* ── DETAILED SUMMARY ────────────────────────────────────────── */}
            {details.detailed_summary && (
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Detailed Summary</h3>
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <p className="text-emerald-700 font-semibold text-xs mb-2 uppercase tracking-wide">Strengths</p>
                    <ul className="space-y-1.5">
                      {(details.detailed_summary.strengths || []).map((s) => (
                        <li key={s} className="flex items-start gap-2 text-xs text-slate-600">
                          <span className="text-emerald-500 mt-0.5 flex-shrink-0">✓</span>{s}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-red-600 font-semibold text-xs mb-2 uppercase tracking-wide">Concerns</p>
                    <ul className="space-y-1.5">
                      {(details.detailed_summary.concerns || []).map((c) => (
                        <li key={c} className="flex items-start gap-2 text-xs text-slate-600">
                          <span className="text-red-400 mt-0.5 flex-shrink-0">!</span>{c}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {details.detailed_summary.interview_focus_areas?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <p className="text-[#2563eb] font-semibold text-xs mb-2.5 uppercase tracking-wide">Interview Focus Areas</p>
                    <div className="grid md:grid-cols-2 gap-2">
                      {details.detailed_summary.interview_focus_areas.map((area, i) => (
                        <div key={area} className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-[#2563eb] flex items-start gap-2">
                          <span className="font-bold text-blue-400 flex-shrink-0">{i + 1}.</span> {area}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {details.detailed_summary.risk_assessment && (
                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-2">
                    <span className="text-xs text-slate-500">Risk Assessment:</span>
                    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${
                      details.detailed_summary.risk_assessment === 'low' ? 'bg-emerald-100 text-emerald-700' :
                      details.detailed_summary.risk_assessment === 'medium' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>{details.detailed_summary.risk_assessment?.toUpperCase()}</span>
                  </div>
                )}
              </div>
            )}

            {/* ── RESUME PREVIEW ──────────────────────────────────────────── */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wider">Resume / Profile Text</h3>
              <pre className="text-xs text-slate-600 whitespace-pre-wrap leading-relaxed max-h-48 overflow-auto bg-slate-50 rounded-lg p-4 border border-slate-100">
                {candidate.resume_text || 'No resume text available.'}
              </pre>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
